import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Mic, Pause, Play, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { storytellerDisplayName } from "../lib/storyteller";
import { Ornament } from "../components/Ornament";

/**
 * PROMPT 4 PART 2 — Conversation screen.
 *
 * Designed for a phone lying flat between two people:
 *   - Chapter kicker + question in large serif (36px+ mobile)
 *   - "Ask gently" follow-ups revealed one at a time
 *   - Big gold Record / Pause button
 *   - Elapsed timer
 *   - "Finish our conversation" button
 *
 * MediaRecorder is timesliced at 20s per chunk. Each chunk is POSTed to
 * /api/conversation/chunk as it arrives, so a signal drop mid-conversation
 * doesn't lose more than the last <20 seconds of audio.
 *
 * Wake Lock API keeps the screen awake on iOS Safari and Android Chrome.
 */
const CHUNK_INTERVAL_MS = 20_000; // 20s slices
const MAX_MS = 60 * 60 * 1000;    // 60-minute hard cap

export default function ConversationScreen() {
  const { day: dayParam } = useParams();
  const day = Number(dayParam);
  const navigate = useNavigate();
  const { user } = useAuth();
  const stName = storytellerDisplayName(user) || "them";
  const isSelf = !!user?.storyteller?.self_story;

  const [prompts, setPrompts] = useState({ question: "", follow_ups: [], title: "" });
  const [convId, setConvId] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | recording | paused | finalizing | done
  const [elapsedMs, setElapsedMs] = useState(0);
  const [followupsShown, setFollowupsShown] = useState(0);
  const [error, setError] = useState("");
  const [needsConsent, setNeedsConsent] = useState(false);
  // Dual-agreement state — see /me/storyteller/consent/agree
  const [askerAgreed, setAskerAgreed] = useState(false);
  const [tellerAgreed, setTellerAgreed] = useState(false);
  const [agreeingRole, setAgreeingRole] = useState(null);   // "asker" | "storyteller"
  const [consentError, setConsentError] = useState("");
  const [noticeVersion, setNoticeVersion] = useState("2026-09-30-v1");

  const mediaRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const uploadingRef = useRef(false);
  const pendingChunksRef = useRef([]);
  const timerRef = useRef(null);
  const wakeLockRef = useRef(null);
  // convId lives in a ref as well as state — the MediaRecorder callbacks are
  // set up BEFORE React re-renders with the new convId, so the state closure
  // is stale.  Reading the ref keeps uploadChunk honest.
  const convIdRef = useRef(null);

  // Load question + follow-ups + consent state on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/story_week/day/${day}/prompts`);
        setPrompts(data);
      } catch (e) {
        setError("Couldn't load this question. Please refresh.");
      }
      // Pin the current notice version so each stored consent record
      // carries the exact text the reader saw.
      try {
        const { data } = await api.get("/me/storyteller/consent/notice");
        if (data?.version) setNoticeVersion(data.version);
      } catch (_) { /* fall back to the hardcoded default */ }
    })();

    // Consent required once per storyteller.  For a self-story we only need
    // the storyteller (which is the user themselves) to agree.
    const st = user?.storyteller;
    if (!st) return;
    const records = st.consent_records || [];
    const askerDone = records.some(r => r.role === "asker");
    const tellerDone = records.some(r => r.role === "storyteller");
    setAskerAgreed(askerDone);
    setTellerAgreed(tellerDone);
    const required = st.self_story
      ? tellerDone
      : (askerDone && tellerDone);
    // Legacy single-tap consent — if the old consent_at is set but the new
    // records are empty, treat it as both agreed so returning users aren't
    // forced through the gate again.
    if (!required && !st.consent_at) {
      setNeedsConsent(true);
    }
  }, [day, user]);

  // Warn before leaving mid-record
  useEffect(() => {
    const handler = (e) => {
      if (status === "recording" || status === "paused") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [status]);

  const acquireWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch (_) { /* not supported — no-op */ }
  };
  const releaseWakeLock = async () => {
    try { await wakeLockRef.current?.release?.(); } catch (_) { /* no-op */ }
    wakeLockRef.current = null;
  };

  const uploadChunk = async (blob, index) => {
    const cid = convIdRef.current;
    if (!cid) return;
    const fd = new FormData();
    fd.append("conv_id", cid);
    fd.append("index", String(index));
    const filename = `chunk_${String(index).padStart(4, "0")}.webm`;
    fd.append("file", new File([blob], filename, { type: blob.type || "audio/webm" }));
    // The axios instance defaults to Content-Type: application/json. For a
    // FormData body we MUST let the browser choose "multipart/form-data;
    // boundary=…" itself — passing an empty object here overrides the default
    // and the browser fills in the boundary before the request goes out.
    await api.post("/conversation/chunk", fd, {
      headers: { "Content-Type": undefined },
    });
  };

  const flushPending = async () => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    try {
      while (pendingChunksRef.current.length) {
        const { blob, index } = pendingChunksRef.current.shift();
        try { await uploadChunk(blob, index); } catch (e) {
          // Requeue for a later retry and stop for now
          pendingChunksRef.current.unshift({ blob, index });
          break;
        }
      }
    } finally {
      uploadingRef.current = false;
    }
  };

  const startRecording = async () => {
    setError("");
    try {
      // 1. Start a new conversation on the server
      const { data } = await api.post("/conversation/start", { day, question: prompts.question });
      // Ref first — MediaRecorder callbacks fire before React re-renders.
      convIdRef.current = data.conv_id;
      setConvId(data.conv_id);

      // 2. Ask for microphone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
                  : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4"
                  : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRef.current = rec;
      chunkIndexRef.current = 0;
      pendingChunksRef.current = [];

      rec.ondataavailable = (evt) => {
        if (!evt.data || evt.data.size === 0) return;
        const idx = chunkIndexRef.current++;
        pendingChunksRef.current.push({ blob: evt.data, index: idx });
        flushPending();
      };

      rec.start(CHUNK_INTERVAL_MS);
      setStatus("recording");
      setElapsedMs(0);
      const startedAt = Date.now();
      timerRef.current = setInterval(() => {
        const el = Date.now() - startedAt;
        setElapsedMs(el);
        if (el >= MAX_MS) finish();  // auto-stop at 60min
      }, 500);
      await acquireWakeLock();
    } catch (e) {
      setError(e?.response?.data?.detail || "We couldn't start the recorder. Please allow microphone access and try again.");
    }
  };

  const pauseResume = () => {
    if (!mediaRef.current) return;
    if (status === "recording") {
      mediaRef.current.pause?.();
      setStatus("paused");
    } else if (status === "paused") {
      mediaRef.current.resume?.();
      setStatus("recording");
    }
  };

  const finish = async () => {
    if (!mediaRef.current) return;
    setStatus("finalizing");
    clearInterval(timerRef.current);
    // Force the recorder to emit any buffered audio as a dataavailable event
    // BEFORE we stop it.  On short recordings (<20s) no chunk has been fired
    // yet, so without this the final blob would never reach the queue.
    try {
      if (mediaRef.current.state !== "inactive" && typeof mediaRef.current.requestData === "function") {
        mediaRef.current.requestData();
      }
    } catch (_) { /* not supported — no-op */ }
    // Wait for both the final dataavailable and the stop event.  Some browsers
    // fire dataavailable AFTER onstop resolves, so we wait for both explicitly.
    await new Promise((resolve) => {
      let gotStop = false;
      let gotData = false;
      const maybeResolve = () => { if (gotStop && gotData) resolve(); };
      const prevData = mediaRef.current.ondataavailable;
      mediaRef.current.ondataavailable = (evt) => {
        try { prevData?.(evt); } catch (_) {}
        gotData = true;
        maybeResolve();
      };
      mediaRef.current.onstop = () => {
        gotStop = true;
        // Give one tick for a trailing dataavailable to arrive.
        setTimeout(() => { gotData = true; maybeResolve(); }, 250);
      };
      // Safety net — never hang the UI more than 4s waiting on the recorder.
      setTimeout(() => { gotStop = true; gotData = true; maybeResolve(); }, 4000);
      try { mediaRef.current.stop(); } catch (_) { gotStop = true; gotData = true; maybeResolve(); }
    });
    // release mic + wake lock
    mediaRef.current.stream?.getTracks?.().forEach(t => t.stop());
    await releaseWakeLock();
    // Make sure all chunks are flushed before finalize
    for (let i = 0; i < 30 && pendingChunksRef.current.length; i++) {
      await flushPending();
      if (pendingChunksRef.current.length) await new Promise(r => setTimeout(r, 500));
    }
    if (pendingChunksRef.current.length) {
      setError("Some parts of the recording didn't upload. Please check your connection and try again.");
      setStatus("recording");
      return;
    }
    try {
      const { data } = await api.post("/conversation/finalize", { conv_id: convIdRef.current });
      if (data.status === "empty") {
        setError(data.message || "We couldn't hear anything on this recording. Please try again.");
        setStatus("idle");
        setConvId(null);
        convIdRef.current = null;
        return;
      }
      // Both "processing" and "ready" navigate to review — review page polls
      // until status flips to ready.
      navigate(`/story-week/day/${day}/review?conv=${data.conv_id}`, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save this conversation just now. Your recording is safe — please try again in a moment.");
      setStatus("recording");
    }
  };

  const cancel = () => {
    if (!confirm("Stop and discard this recording?")) return;
    clearInterval(timerRef.current);
    try { mediaRef.current?.stop?.(); } catch (_) {}
    mediaRef.current?.stream?.getTracks?.().forEach(t => t.stop());
    releaseWakeLock();
    navigate(`/story-week/day/${day}`, { replace: true });
  };

  const agreeAs = async (role) => {
    setAgreeingRole(role);
    setConsentError("");
    try {
      const { data } = await api.post("/me/storyteller/consent/agree", {
        role,
        notice_version: noticeVersion,
      });
      if (role === "asker") setAskerAgreed(true);
      if (role === "storyteller") setTellerAgreed(true);
      if (data?.ready) setNeedsConsent(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setConsentError(typeof detail === "string" ? detail : "Couldn't save that just now. Please try again.");
    } finally {
      setAgreeingRole(null);
    }
  };

  const mm = String(Math.floor(elapsedMs / 60000)).padStart(2, "0");
  const ss = String(Math.floor((elapsedMs % 60000) / 1000)).padStart(2, "0");

  // Dual-agreement consent — one screen, one "I agree" button per person.
  if (needsConsent) {
    const selfStory = user?.storyteller?.self_story;
    const bothDone = selfStory ? tellerAgreed : (askerAgreed && tellerAgreed);
    const askerLabel = ((user?.name || "").split(" ")[0] || "you").trim();

    return (
      <div className="min-h-screen paper" data-testid="conv-consent-page">
        <header className="px-6 py-5 flex items-center justify-between">
          <button onClick={() => navigate(`/story-week/day/${day}`)} className="text-[#8C8278] hover:text-[#111D33] transition p-2 -ml-2" style={{ minHeight: 44, minWidth: 44 }} data-testid="conv-consent-back">
            <X size={22} />
          </button>
          <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.35em]">A GENTLE AGREEMENT</p>
          <div style={{ width: 44 }} />
        </header>

        <main className="max-w-2xl mx-auto px-6 pb-16">
          <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.3em] text-center mb-6">BEFORE WE BEGIN</p>
          <h1 className="text-center text-[#111D33]"
              style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "clamp(2rem, 6vw, 2.75rem)", lineHeight: 1.2 }}
              data-testid="conv-consent-heading">
            {selfStory
              ? "Just before you begin your story."
              : `Just before ${stName || "you both"} begin.`}
          </h1>

          <div className="mt-8 bg-white hairline-cream p-6 sm:p-8" style={{ borderRadius: 4 }}>
            <p className="text-[#1B2A4A] mb-4" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.7 }}>
              We're about to record your conversation so it can become a chapter of your Living Book. Here's what happens next, in plain words:
            </p>
            <ul className="space-y-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "16px", lineHeight: 1.7 }} data-testid="conv-consent-notice">
              <li className="flex gap-3"><span className="text-[#B8973A]">·</span><span><b>The audio is recorded on this phone</b> and safely stored in your private Living Book account.</span></li>
              <li className="flex gap-3"><span className="text-[#B8973A]">·</span><span><b>The words are transcribed</b> — an AI service (OpenAI Whisper) turns speech into text, then gently tidies it into a story.</span></li>
              <li className="flex gap-3"><span className="text-[#B8973A]">·</span><span><b>Only {selfStory ? "you" : `${askerLabel} and the people ${askerLabel} chooses to share with`} can listen</b> — it is never public and never sold.</span></li>
              <li className="flex gap-3"><span className="text-[#B8973A]">·</span><span><b>Removal is easy</b> — email <a href="mailto:hello@thelivingbook.io" className="text-[#B8973A] underline underline-offset-2">hello@thelivingbook.io</a> anytime and we will delete the recording and any words from it.</span></li>
            </ul>
          </div>

          {consentError && (
            <div className="mt-4 p-3" style={{ background: "#FDECEC", border: "1px solid #E8B4B4", borderRadius: 4 }} role="alert" data-testid="conv-consent-error">
              <p className="text-[#8A1F1F]" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }}>{consentError}</p>
            </div>
          )}

          {/* Two big buttons — one per person. In a self-story there's only one. */}
          <div className={`mt-8 grid ${selfStory ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"} gap-4`} data-testid="conv-consent-buttons">
            {!selfStory && (
              <div className="bg-white hairline-cream p-6 flex flex-col items-center text-center" style={{ borderRadius: 4 }} data-testid="conv-consent-asker-card">
                <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em]">THE ASKER</p>
                <p className="mt-2 text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.4rem", fontStyle: "italic" }}>
                  {askerLabel}
                </p>
                {askerAgreed ? (
                  <div className="mt-4 flex items-center gap-2 text-[#3D5C2E]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }} data-testid="conv-consent-asker-done">
                    <span aria-hidden="true">✓</span> I agree
                  </div>
                ) : (
                  <button type="button" onClick={() => agreeAs("asker")} disabled={agreeingRole === "asker"}
                          className="btn-gold mt-5 w-full"
                          style={{ minHeight: 72, fontSize: "16px", letterSpacing: "0.2em", padding: "18px 24px" }}
                          data-testid="conv-consent-asker-btn">
                    {agreeingRole === "asker" ? "SAVING…" : "I AGREE"}
                  </button>
                )}
              </div>
            )}

            <div className="bg-white hairline-cream p-6 flex flex-col items-center text-center" style={{ borderRadius: 4 }} data-testid="conv-consent-teller-card">
              <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em]">{selfStory ? "YOUR AGREEMENT" : "THE STORYTELLER"}</p>
              <p className="mt-2 text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.4rem", fontStyle: "italic" }}>
                {selfStory ? askerLabel : (stName || "the storyteller")}
              </p>
              {tellerAgreed ? (
                <div className="mt-4 flex items-center gap-2 text-[#3D5C2E]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }} data-testid="conv-consent-teller-done">
                  <span aria-hidden="true">✓</span> I agree
                </div>
              ) : (
                <button type="button" onClick={() => agreeAs("storyteller")} disabled={agreeingRole === "storyteller"}
                        className="btn-gold mt-5 w-full"
                        style={{ minHeight: 72, fontSize: "16px", letterSpacing: "0.2em", padding: "18px 24px" }}
                        data-testid="conv-consent-teller-btn">
                  {agreeingRole === "storyteller" ? "SAVING…" : "I AGREE"}
                </button>
              )}
            </div>
          </div>

          {bothDone ? (
            <div className="mt-8 text-center" data-testid="conv-consent-ready">
              <p className="italic text-[#3D5C2E] mb-4" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
                Thank you. When you're ready, tap below to begin.
              </p>
              <button type="button" onClick={() => setNeedsConsent(false)}
                      className="btn-gold"
                      style={{ minHeight: 60, padding: "16px 40px", fontSize: "14px", letterSpacing: "0.25em" }}
                      data-testid="conv-consent-continue-btn">
                CONTINUE TO RECORDING &rarr;
              </button>
            </div>
          ) : (
            <p className="mt-8 text-center italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
              {selfStory
                ? "Tap I agree above when you're ready to begin."
                : "Both taps together — no rush."}
            </p>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col paper" data-testid="conv-screen">
      {/* Top bar */}
      <header className="px-6 py-5 flex items-center justify-between">
        <button onClick={cancel} className="text-[#8C8278] hover:text-[#111D33] transition p-2 -ml-2" style={{ minHeight: 44, minWidth: 44 }} data-testid="conv-close">
          <X size={22} />
        </button>
        <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.35em]" data-testid="conv-kicker">
          {prompts.title || `DAY ${day}`}
        </p>
        <div style={{ width: 44 }} />
      </header>

      {/* Big question */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
        <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.3em] text-center mb-6" data-testid="conv-day-kicker">
          DAY {day} &middot; STORY WEEK
        </p>
        <h1 className="text-center max-w-2xl"
            style={{ fontFamily: "Cormorant Garamond, Georgia, serif", fontWeight: 300,
                     fontSize: "clamp(2.25rem, 7vw, 3.25rem)", lineHeight: 1.15, color: "#111D33" }}
            data-testid="conv-question">
          {prompts.question || "Loading…"}
        </h1>

        <Ornament className="my-10" color="#B8973A" />

        {/* Ask gently — reveal one at a time */}
        {prompts.follow_ups?.length > 0 && (
          <div className="w-full max-w-md" data-testid="conv-followups">
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em] text-center">ASK GENTLY</p>
            <div className="mt-4 space-y-3 text-center">
              {prompts.follow_ups.slice(0, followupsShown).map((f, i) => (
                <p key={i} className="italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.25rem", lineHeight: 1.5, color: "#5C5548" }}
                   data-testid={`conv-followup-${i}`}>
                  {f}
                </p>
              ))}
            </div>
            {followupsShown < prompts.follow_ups.length && (
              <button onClick={() => setFollowupsShown((n) => n + 1)}
                      className="mt-5 mx-auto block text-[#B8973A] hover:text-[#8C6A20] transition"
                      style={{ fontFamily: "Georgia, serif", fontSize: "13px", letterSpacing: "0.2em", minHeight: 44 }}
                      data-testid="conv-followup-reveal">
                {followupsShown === 0 ? "Reveal a gentle nudge" : "Reveal another"}
              </button>
            )}
          </div>
        )}
      </main>

      {/* Bottom controls */}
      <footer className="px-6 py-8 flex flex-col items-center gap-5" style={{ background: "#F7F0E0", borderTop: "1px solid #E8D5A3" }}>
        <p className="label-ui text-[24px] tracking-[0.15em]" style={{ color: "#8C6A20", fontFamily: "Georgia, serif" }} data-testid="conv-timer">
          {mm}:{ss}
        </p>

        {status === "idle" && (
          <button onClick={startRecording}
                  className="btn-gold w-full max-w-sm"
                  style={{ minHeight: 72, padding: "20px 40px", fontSize: "15px", letterSpacing: "0.3em", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12 }}
                  data-testid="conv-start-btn">
            <Mic size={22} /> START RECORDING
          </button>
        )}

        {(status === "recording" || status === "paused") && (
          <>
            <button onClick={pauseResume}
                    className="btn-gold w-full max-w-sm"
                    style={{ minHeight: 72, padding: "20px 40px", fontSize: "15px", letterSpacing: "0.3em", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12 }}
                    data-testid="conv-pause-btn">
              {status === "recording" ? (<><Pause size={22} /> PAUSE</>) : (<><Play size={22} /> RESUME</>)}
            </button>
            <button onClick={finish}
                    className="hairline-cream bg-white text-[#1B2A4A] hover:bg-[#FAF7F2] transition px-6 py-3"
                    style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "14px", letterSpacing: "0.15em", minHeight: 48 }}
                    data-testid="conv-finish-btn">
              FINISH OUR CONVERSATION
            </button>
          </>
        )}

        {status === "finalizing" && (
          <p className="italic text-[#5C5548]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }} data-testid="conv-finalizing">
            Turning your conversation into a chapter{isSelf ? "…" : ` of ${stName}'s book…`}
          </p>
        )}

        {error && <p className="text-[#8A1F1F] max-w-md text-center" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }} role="alert" data-testid="conv-error">{error}</p>}
      </footer>
    </div>
  );
}
